/**
 * CENTRALIZED CONFIGURATION v1.0
 * 
 * This is the SINGLE SOURCE OF TRUTH for all rank, token, and styling configurations.
 * All systems must reference this configuration - never hardcode values elsewhere.
 * 
 * Editable via Admin Dashboard → System Configuration
 */

import { AccountTier } from '../types';

// ==================== RANK/TIER CONFIGURATION ====================

export interface RankConfig {
    id: AccountTier;
    name: string;
    displayName: string; // For UI display (e.g., "Premium" or "⭐ Premium")
    description: string;

    // Pricing (in PHP)
    monthlyPrice: number;
    yearlyPrice: number;

    // Token System
    tokenLimit: number; // Daily token limit (-1 for unlimited)
    tokenRefreshRate: number; // Tokens replenished per refresh interval
    refreshIntervalMinutes: number; // How often tokens refresh (default: 60)

    // Feature Limits
    dailyAttachmentLimit: number; // -1 for unlimited
    maxFileSizeMB: number;
    maxSessionHistory: number; // -1 for unlimited

    // AI Configuration
    defaultModel: string;
    advancedModelsAccess: boolean;
    priorityQueue: boolean;

    // Features list (displayed in upgrade modals)
    features: string[];

    // Styling
    colors: {
        primary: string; // Main color (hex)
        secondary: string; // Secondary color
        gradientFrom: string;
        gradientTo: string;
        text: string; // Text color for badges
        background: string; // Background color for badges
        chatBubble: string; // User's chat bubble color
        chatBubbleText: string; // User's chat bubble text color
        avatarBorder: string; // Border color for avatar
        glowColor: string; // Glow effect color (for animations)
    };

    // Badge styling
    badge: {
        text: string; // Badge text (e.g., "⭐ Premium")
        icon: string; // Icon emoji
        showInHeader: boolean;
        showInSidebar: boolean;
    };

    // Ordering & visibility
    displayOrder: number;
    isActive: boolean;
    showInUpgradeModal: boolean;
}

export interface TokenConsumptionConfig {
    // Base costs per message type
    baseCosts: {
        simpleQuestion: number; // Basic question (~100 tokens)
        moderateQuestion: number; // Medium complexity (~300 tokens)
        complexQuestion: number; // Complex/multi-part (~600 tokens)
        codeGeneration: number; // Code writing (~500 tokens)
        documentAnalysis: number; // PDF/image analysis (~800 tokens)
    };

    // Multipliers
    multipliers: {
        withAttachment: number; // Multiplier when attachment included (1.5x)
        longResponse: number; // For responses > 1000 words (1.3x)
        reasoning: number; // For chain-of-thought responses (1.2x)
        followUp: number; // For follow-up questions in same context (0.8x)
    };

    // Attachment analysis costs
    attachments: {
        imageAnalysis: number; // Per image analyzed
        pdfAnalysis: number; // Per PDF page (base)
        pdfPerPage: number; // Additional per page after first
        textFileAnalysis: number; // Per text/code file
        spreadsheetAnalysis: number; // Per spreadsheet
        maxPagesPerDocument: number; // Max pages to analyze
    };

    // Quiz & Exam generation costs
    quizExam: {
        quizGenerationBase: number; // Base cost for quiz generation
        perQuizQuestion: number; // Additional per question generated
        examGenerationBase: number; // Base cost for full exam
        perExamQuestion: number; // Additional per exam question
        mockExamSetup: number; // Cost for mock exam configuration
        examGrading: number; // Cost for AI grading of exam
        explanationGeneration: number; // Cost for detailed explanations
        difficultyAdjustment: number; // Extra cost for difficulty analysis
    };

    // Essay & Writing features
    writing: {
        essayGradingBase: number; // Base cost for essay grading
        essayPerWord: number; // Cost per 100 words graded
        writingAssistance: number; // General writing help
        grammarCheck: number; // Grammar and spelling check
        plagiarismCheck: number; // Plagiarism detection
    };

    // Export costs
    exports: {
        singleDocx: number; // Single message export to DOCX
        singlePdf: number; // Single message export to PDF
        compiledDocx: number; // Multiple messages to DOCX
        compiledPdf: number; // Multiple messages to PDF
        perAdditionalResponse: number; // Per additional response in compilation
        sessionExport: number; // Export entire session
        formattedReport: number; // Formatted report generation
    };

    // Special features
    specialFeatures: {
        flashcardGeneration: number; // Per flashcard set
        summaryGeneration: number; // Document/session summary
        mindMapGeneration: number; // Mind map creation
        studyPlanGeneration: number; // Personalized study plan
        conceptExplanation: number; // Deep concept explanation
        problemSolving: number; // Step-by-step problem solving
        caseAnalysis: number; // Case study analysis
    };

    // Accounting-specific features
    accountingFeatures: {
        journalEntryGeneration: number; // Create journal entries
        financialStatementAnalysis: number; // Analyze FS
        taxComputation: number; // Tax calculation assistance
        auditProcedures: number; // Audit procedure guidance
        standardsReference: number; // PFRS/PAS lookup
        ratioAnalysis: number; // Financial ratio calculations
    };

    // Warning thresholds
    warningThreshold: number; // Show warning if single request exceeds this
    criticalThreshold: number; // Block or require confirmation if exceeds this

    // Free tier limits (shown in UI)
    freeOperationsPerDay: {
        questions: number;
        quizzes: number;
        exports: number;
        attachments: number;
    };
}

export interface UIConfig {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    fontFamily: 'inter' | 'space-grotesk' | 'plus-jakarta' | 'outfit' | 'satoshi';
    borderRadius: 'sharp' | 'rounded' | 'pill';
    logoUrl: string;
    faviconUrl: string;
    darkMode: boolean; // Default setting
}

export interface FeatureFlags {
    enableChat: boolean;
    enableQuiz: boolean;
    enableFlashcards: boolean;
    enableTools: boolean; // Calculator, Notes
    enableCommunity: boolean;
    enableShop: boolean;
    enableLegalEthics: boolean; // Specific subject toggle
    enableTaxation: boolean;
}

export interface AppContentConfig {
    appName: string;
    tagline: string;
    sidebarLabels: {
        dashboard: string;
        chat: string;
        quiz: string;
        flashcards: string;
        tools: string;
        shop: string;
        profile: string;
    };
    welcomeMessage: {
        title: string;
        subtitle: string;
    };
}

export interface SystemConfig {
    // Maintenance
    maintenanceMode: boolean;
    maintenanceMessage: string;
    maintenanceETA: string | null;

    // Guest/Free limits
    guestMessageLimit: number;
    guestDailyLimit: number;

    // Auto-budget settings
    autoBudget: {
        enabled: boolean;
        minPerRequest: number;
        maxPerRequest: number;
        defaultPerRequest: number;
    };

    // Session settings
    maxSessionsPerUser: number; // -1 for unlimited
    sessionExpiryDays: number;

    // API Configuration
    defaultApiModel: string;
    fallbackApiModel: string;

    // Welcome/Onboarding
    welcome: {
        title: string;
        message: string;
        features: string[];
    };

    // Sale/Discounts
    sale: {
        isActive: boolean;
        discountPercent: number;
        endDate: string | null;
    };

    // Extension discounts (for premium extensions)
    extensionDiscounts: {
        months3: number;
        months6: number;
        months12: number;
    };

    // New Configuration Sections
    ui: UIConfig;
    features: FeatureFlags;
    content: AppContentConfig;
}

// ==================== LANDING PAGE CONFIGURATION ====================

export interface LandingPageContentConfig {
    // Enable/Disable
    enabled: boolean;

    // Theme & Style
    theme: {
        primaryColor: string; // Hex color
        accentColor: string;
        darkMode: boolean;
        fontFamily: 'inter' | 'space-grotesk' | 'plus-jakarta' | 'outfit' | 'satoshi';
        borderRadius: 'sharp' | 'rounded' | 'pill';
        animationStyle: 'subtle' | 'playful' | 'dramatic';
    };

    // Layout Options
    layout: {
        showNavigation: boolean;
        showHeroAnimation: boolean;
        showDemoChat: boolean;
        showSubjectsCarousel: boolean;
        compactMobile: boolean;
        maxWidth: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    };

    // Hero Section
    hero: {
        badge: string;
        badgeIcon: string; // emoji or icon name
        title: string;
        titleHighlight: string;
        description: string;
        primaryCTA: string;
        primaryCTAIcon: string;
        secondaryCTA: string;
        trustBadges: string[];
        showRating: boolean;
        ratingValue: string;
        ratingCount: string;
    };

    // Stats Section
    stats: {
        enabled: boolean;
        students: string;
        studentsLabel: string;
        passRate: string;
        passRateLabel: string;
        questionsAnswered: string;
        questionsLabel: string;
        rating: string;
        ratingLabel: string;
    };

    // Subjects (CPALE coverage)
    subjects: {
        code: string;
        name: string;
        color: string;
        icon: string;
    }[];

    // Features
    features: {
        icon: string;
        title: string;
        description: string;
        bulletPoints: string[];
        image?: string; // Optional image URL
    }[];

    // Testimonials
    testimonials: {
        quote: string;
        author: string;
        role: string;
        avatar: string; // Initials or image URL
        rating: number;
        verified: boolean;
    }[];

    // Pricing Display
    pricing: {
        enabled: boolean;
        showToggle: boolean;
        defaultBilling: 'monthly' | 'yearly';
        showGuarantee: boolean;
        guaranteeText: string;
    };

    // FAQ
    faqs: {
        question: string;
        answer: string;
    }[];

    // Final CTA
    finalCTA: {
        enabled: boolean;
        title: string;
        description: string;
        buttonText: string;
        subtext: string;
        showConfetti: boolean;
    };

    // Footer
    footer: {
        copyright: string;
        links: { label: string; url: string }[];
        showSocialLinks: boolean;
        socialLinks: { platform: string; url: string }[];
    };

    // SEO
    seo: {
        title: string;
        description: string;
        keywords: string[];
    };

    // Advanced
    advanced: {
        enableAnalytics: boolean;
        customCSS: string;
        customJS: string;
    };
}

export const DEFAULT_LANDING_CONFIG: LandingPageContentConfig = {
    enabled: true,

    theme: {
        primaryColor: '#f59e0b', // Amber
        accentColor: '#6366f1', // Indigo
        darkMode: true,
        fontFamily: 'outfit',
        borderRadius: 'rounded',
        animationStyle: 'playful'
    },

    layout: {
        showNavigation: true,
        showHeroAnimation: true,
        showDemoChat: true,
        showSubjectsCarousel: true,
        compactMobile: true,
        maxWidth: 'lg'
    },

    hero: {
        badge: 'AI-Powered CPA Review',
        badgeIcon: '✨',
        title: 'Pass Your CPA Exam',
        titleHighlight: 'With Confidence',
        description: 'Meet Luca — your 24/7 AI study companion designed specifically for the Philippine CPA Licensure Examination.',
        primaryCTA: 'Start Free Trial',
        primaryCTAIcon: '🚀',
        secondaryCTA: 'See How It Works',
        trustBadges: ['No credit card', 'Secure & Private', 'All devices'],
        showRating: true,
        ratingValue: '4.9',
        ratingCount: '2,000+'
    },

    stats: {
        enabled: true,
        students: '10K+',
        studentsLabel: 'Active Students',
        passRate: '92%',
        passRateLabel: 'Pass Rate',
        questionsAnswered: '1M+',
        questionsLabel: 'Questions',
        rating: '4.9/5',
        ratingLabel: 'Rating'
    },

    subjects: [
        { code: 'FAR', name: 'Financial Accounting & Reporting', color: '#3b82f6', icon: 'BookOpen' },
        { code: 'AFAR', name: 'Advanced Financial Accounting', color: '#8b5cf6', icon: 'Layers' },
        { code: 'MAS', name: 'Management Advisory Services', color: '#f59e0b', icon: 'BarChart2' },
        { code: 'AUD', name: 'Auditing', color: '#10b981', icon: 'Eye' },
        { code: 'TAX', name: 'Taxation', color: '#ef4444', icon: 'Calculator' },
        { code: 'RFBT', name: 'Regulatory Framework', color: '#6366f1', icon: 'Shield' }
    ],

    features: [
        {
            icon: 'Brain',
            title: 'AI That Thinks Like a CPA Reviewer',
            description: 'Unlike generic AI assistants, Luca is specifically trained on Philippine accounting standards.',
            bulletPoints: [
                'Deep understanding of PFRS/PAS and Philippine tax laws',
                'Step-by-step problem solving with complete workings',
                'References to actual standards and legal provisions'
            ]
        },
        {
            icon: 'FileText',
            title: 'Upload Documents, Get Instant Analysis',
            description: 'Drop your PDF reviewers or screenshots of problems for detailed explanations.',
            bulletPoints: [
                'Supports PDF, images, PowerPoint, and text files',
                'Extracts and analyzes tables and computations',
                'Cross-references with accounting standards'
            ]
        },
        {
            icon: 'GraduationCap',
            title: 'Generate Unlimited Practice Quizzes',
            description: 'Create custom quizzes on any topic with instant AI grading.',
            bulletPoints: [
                'Multiple choice, true/false, and computational',
                'Adjustable difficulty levels',
                'Instant scoring with explanations'
            ]
        }
    ],

    testimonials: [
        {
            quote: 'Luca helped me understand complex PFRS concepts I\'ve been struggling with for months.',
            author: 'Maria Santos',
            role: 'CPA Candidate, Batch 2024',
            avatar: 'MS',
            rating: 5,
            verified: true
        },
        {
            quote: 'The quiz generation feature is a game-changer. Passed my board exam on the first try!',
            author: 'John Reyes',
            role: 'Newly Licensed CPA',
            avatar: 'JR',
            rating: 5,
            verified: true
        },
        {
            quote: 'As a working professional, Luca is available 24/7 and never gets tired of my questions.',
            author: 'Ana Cruz',
            role: 'Senior Accountant',
            avatar: 'AC',
            rating: 5,
            verified: true
        }
    ],

    pricing: {
        enabled: true,
        showToggle: true,
        defaultBilling: 'yearly',
        showGuarantee: true,
        guaranteeText: '7-day money-back guarantee'
    },

    faqs: [
        {
            question: 'What are tokens and how do they work?',
            answer: 'Tokens are the currency for AI interactions. Each question uses tokens based on complexity. Your tokens automatically refresh every hour.'
        },
        {
            question: 'Can I use Luca to prepare for the actual CPALE?',
            answer: 'Absolutely! Luca covers all 6 subjects based on current standards and laws including CREATE Law and PFRS updates.'
        },
        {
            question: 'Is my data secure and private?',
            answer: 'Yes. All your conversations are encrypted and stored securely. We never share your personal data with third parties.'
        }
    ],

    finalCTA: {
        enabled: true,
        title: 'Ready to Ace Your CPA Exam?',
        description: 'Join thousands of students using Luca to prepare smarter.',
        buttonText: 'Get Started Free',
        subtext: 'No credit card required • Cancel anytime',
        showConfetti: true
    },

    footer: {
        copyright: '© 2024 Luca AI. All rights reserved.',
        links: [
            { label: 'Privacy', url: '#' },
            { label: 'Terms', url: '#' },
            { label: 'Contact', url: '#' }
        ],
        showSocialLinks: false,
        socialLinks: []
    },

    seo: {
        title: 'Luca - AI-Powered CPA Review Assistant',
        description: 'Your 24/7 AI study companion for the Philippine CPA Licensure Examination.',
        keywords: ['CPA', 'CPALE', 'Philippine CPA', 'accounting', 'review', 'AI tutor']
    },

    advanced: {
        enableAnalytics: true,
        customCSS: '',
        customJS: ''
    }
};

// ==================== DEFAULT CONFIGURATIONS ====================

export const DEFAULT_RANK_CONFIGS: RankConfig[] = [
    {
        id: 'standard',
        name: 'Standard',
        displayName: 'Standard',
        description: 'Free tier with essential features',

        monthlyPrice: 0,
        yearlyPrice: 0,

        tokenLimit: 2000,           // ~40 simple messages/day
        tokenRefreshRate: 200,      // Replenish 200 tokens/hour
        refreshIntervalMinutes: 60,

        dailyAttachmentLimit: 3,
        maxFileSizeMB: 5,
        maxSessionHistory: 50,

        defaultModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: false,
        priorityQueue: false,

        features: [
            '2,000 tokens per day',
            '200 tokens/hour refresh',
            '3 attachments per day',
            'Basic AI model access',
            'Standard support'
        ],

        colors: {
            primary: '#64748b', // slate-500
            secondary: '#94a3b8', // slate-400
            gradientFrom: '#94a3b8',
            gradientTo: '#64748b',
            text: '#ffffff',
            background: 'rgba(100, 116, 139, 0.2)',
            chatBubble: '#475569', // slate-600
            chatBubbleText: '#ffffff',
            avatarBorder: '#64748b',
            glowColor: 'rgba(100, 116, 139, 0.3)',
        },

        badge: {
            text: 'Standard',
            icon: '👤',
            showInHeader: false,
            showInSidebar: true,
        },

        displayOrder: 1,
        isActive: true,
        showInUpgradeModal: false,
    },
    {
        id: 'premium',
        name: 'Premium',
        displayName: 'Premium',
        description: 'Enhanced features for serious learners',

        monthlyPrice: 149, // PHP - Student-friendly pricing (₱5/day = less than coffee)
        yearlyPrice: 1199, // PHP - ₱100/month (save 30%+)

        tokenLimit: 15000,          // ~300 messages/day
        tokenRefreshRate: 1500,     // Replenish 1,500 tokens/hour
        refreshIntervalMinutes: 60,

        dailyAttachmentLimit: -1, // Unlimited
        maxFileSizeMB: 50,
        maxSessionHistory: -1, // Unlimited

        defaultModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: true,
        priorityQueue: true,

        features: [
            '15,000 tokens per day',
            '1,500 tokens/hour refresh',
            'Unlimited attachments',
            'All AI models access',
            'Priority support',
            'Export to PDF/DOCX'
        ],

        colors: {
            primary: '#f59e0b', // amber-500
            secondary: '#fbbf24', // amber-400
            gradientFrom: '#fbbf24',
            gradientTo: '#f59e0b',
            text: '#1e293b',
            background: 'rgba(245, 158, 11, 0.2)',
            chatBubble: '#d97706', // amber-600
            chatBubbleText: '#ffffff',
            avatarBorder: '#f59e0b',
            glowColor: 'rgba(245, 158, 11, 0.4)',
        },

        badge: {
            text: '⭐ Premium',
            icon: '👑',
            showInHeader: true,
            showInSidebar: true,
        },

        displayOrder: 2,
        isActive: true,
        showInUpgradeModal: true,
    },
    {
        id: 'max',
        name: 'MAX',
        displayName: 'MAX',
        description: 'Ultimate power for professionals',

        monthlyPrice: 399, // PHP - Professional pricing (₱13/day)
        yearlyPrice: 2999, // PHP - ₱250/month (save 40%+)

        tokenLimit: 50000,          // ~1000 messages/day
        tokenRefreshRate: 5000,     // Replenish 5,000 tokens/hour
        refreshIntervalMinutes: 60,

        dailyAttachmentLimit: -1, // Unlimited
        maxFileSizeMB: 100,
        maxSessionHistory: -1, // Unlimited

        defaultModel: 'gemini-2.5-flash-lite',
        advancedModelsAccess: true,
        priorityQueue: true,

        features: [
            '50,000 tokens per day',
            '5,000 tokens/hour refresh',
            'Unlimited everything',
            'All AI models + priority',
            'VIP support',
            'Early feature access'
        ],

        colors: {
            primary: '#ec4899', // pink-500
            secondary: '#f472b6', // pink-400
            gradientFrom: '#a855f7', // purple-500
            gradientTo: '#ec4899', // pink-500
            text: '#ffffff',
            background: 'rgba(236, 72, 153, 0.2)',
            chatBubble: '#be185d', // pink-700
            chatBubbleText: '#ffffff',
            avatarBorder: '#ec4899',
            glowColor: 'rgba(236, 72, 153, 0.5)',
        },

        badge: {
            text: '💎 MAX',
            icon: '💎',
            showInHeader: true,
            showInSidebar: true,
        },

        displayOrder: 3,
        isActive: true,
        showInUpgradeModal: true,
    }
];

export const DEFAULT_TOKEN_CONSUMPTION_CONFIG: TokenConsumptionConfig = {
    baseCosts: {
        simpleQuestion: 100,
        moderateQuestion: 300,
        complexQuestion: 600,
        codeGeneration: 500,
        documentAnalysis: 800,
    },

    multipliers: {
        withAttachment: 1.5,
        longResponse: 1.3,
        reasoning: 1.2,
        followUp: 0.8,
    },

    attachments: {
        imageAnalysis: 500,
        pdfAnalysis: 300,
        pdfPerPage: 100,
        textFileAnalysis: 200,
        spreadsheetAnalysis: 400,
        maxPagesPerDocument: 50,
    },

    quizExam: {
        quizGenerationBase: 200,
        perQuizQuestion: 50,
        examGenerationBase: 500,
        perExamQuestion: 75,
        mockExamSetup: 300,
        examGrading: 400,
        explanationGeneration: 150,
        difficultyAdjustment: 100,
    },

    writing: {
        essayGradingBase: 200,
        essayPerWord: 5,
        writingAssistance: 300,
        grammarCheck: 100,
        plagiarismCheck: 500,
    },

    exports: {
        singleDocx: 50,
        singlePdf: 75,
        compiledDocx: 150,
        compiledPdf: 200,
        perAdditionalResponse: 25,
        sessionExport: 300,
        formattedReport: 400,
    },

    specialFeatures: {
        flashcardGeneration: 400,
        summaryGeneration: 250,
        mindMapGeneration: 350,
        studyPlanGeneration: 500,
        conceptExplanation: 300,
        problemSolving: 400,
        caseAnalysis: 600,
    },

    accountingFeatures: {
        journalEntryGeneration: 200,
        financialStatementAnalysis: 500,
        taxComputation: 400,
        auditProcedures: 350,
        standardsReference: 150,
        ratioAnalysis: 250,
    },

    warningThreshold: 5000,
    criticalThreshold: 25000,

    freeOperationsPerDay: {
        questions: 500,
        quizzes: 10,
        exports: 5,
        attachments: 3,
    },
};

export const DEFAULT_SYSTEM_CONFIG: SystemConfig = {
    maintenanceMode: false,
    maintenanceMessage: 'Luca is currently undergoing maintenance. Please check back soon!',
    maintenanceETA: null,

    guestMessageLimit: 3,
    guestDailyLimit: 5,

    autoBudget: {
        enabled: true,
        minPerRequest: 300,
        maxPerRequest: 125000,
        defaultPerRequest: 5000,
    },

    maxSessionsPerUser: -1,
    sessionExpiryDays: 90,

    defaultApiModel: 'gemini-2.5-flash-lite',
    fallbackApiModel: 'gemini-2.5-flash-lite',

    welcome: {
        title: 'Welcome to Luca AI',
        message: 'Your personal AI-powered CPA Board Exam reviewer.',
        features: [
            '24/7 CPA Review Assistant',
            'Smart Token System',
            'PFRS/PAS & Tax Law Citations',
            'Detailed Problem Solving'
        ],
    },

    sale: {
        isActive: false,
        discountPercent: 0,
        endDate: null,
    },

    extensionDiscounts: {
        months3: 10,
        months6: 20,
        months12: 30,
    },

    ui: {
        primaryColor: '#f59e0b', // Amber-500
        secondaryColor: '#64748b', // Slate-500
        accentColor: '#6366f1', // Indigo-500
        fontFamily: 'outfit',
        borderRadius: 'rounded',
        logoUrl: '/logo.png',
        faviconUrl: '/favicon.ico',
        darkMode: false,
    },

    features: {
        enableChat: true,
        enableQuiz: true,
        enableFlashcards: true,
        enableTools: true,
        enableCommunity: false, // Coming soon
        enableShop: true,
        enableLegalEthics: true,
        enableTaxation: true,
    },

    content: {
        appName: 'Luca',
        tagline: 'AI-Powered CPA Review Assistant',
        sidebarLabels: {
            dashboard: 'Dashboard',
            chat: 'AI Tutor',
            quiz: 'Practice Quiz',
            flashcards: 'Flashcards',
            tools: 'Study Tools',
            shop: 'Shop',
            profile: 'Profile',
        },
        welcomeMessage: {
            title: 'Welcome back, {name}!',
            subtitle: 'Ready to continue your CPA journey?',
        },
    },


};

// ==================== SHOP ITEMS CONFIGURATION ====================

export interface ShopBenefit {
    id: string;
    icon: string; // Lucide icon name
    title: string;
    subtitle: string;
    color?: 'slate' | 'blue' | 'amber' | 'purple' | 'emerald' | 'pink' | 'red' | 'orange';
}

export interface ShopTokenPackage {
    id: string;
    name: string;
    tokens: number;
    price: number;
    originalPrice?: number;
    savings?: number;
    description: string;
    perks: string[];
    iconType: 'coffee' | 'book' | 'crown' | 'diamond' | 'star' | 'zap' | 'sparkles' | 'gift' | 'rocket' | 'fire';
    color: 'slate' | 'blue' | 'amber' | 'purple' | 'emerald' | 'pink' | 'red' | 'orange';
    badge?: string;
    badgeColor?: 'slate' | 'blue' | 'amber' | 'purple' | 'emerald' | 'pink' | 'red' | 'orange';
    popular?: boolean;
    highlight?: boolean;
    isActive: boolean;
    sale?: {
        isActive: boolean;
        salePrice: number;
        saleBadge: string;
        saleEnds?: string;
    };
    displayOrder: number;
}

export interface ShopUnlimitedPackage {
    id: string;
    name: string;
    duration: number; // in minutes
    price: number;
    description: string;
    isActive: boolean;
    popular?: boolean;
    displayOrder: number;
    sale?: {
        isActive: boolean;
        salePrice: number;
        saleBadge: string;
        saleEnds?: string;
    };
}

export interface ShopPremiumPackage {
    id: string;
    name: string;
    duration: number; // in days
    price: number;
    originalPrice?: number;
    description: string;
    perks: string[];
    savings?: number;
    isActive: boolean;
    popular?: boolean;
    displayOrder: number;
    sale?: {
        isActive: boolean;
        salePrice: number;
        saleBadge: string;
        saleEnds?: string;
    };
}

export interface ShopMaxPackage {
    id: string;
    name: string;
    duration: number; // in days
    price: number;
    originalPrice?: number;
    description: string;
    perks: string[];
    savings?: number;
    isActive: boolean;
    popular?: boolean;
    displayOrder: number;
    sale?: {
        isActive: boolean;
        salePrice: number;
        saleBadge: string;
        saleEnds?: string;
    };
}

export interface ShopGlobalSale {
    isActive: boolean;
    discountPercent: number;
    title: string;
    endsAt: string | null;
}

export interface ShopDisplaySettings {
    showDescriptions: boolean;
    condensedView?: boolean;
    showIcons?: boolean;
    gridColumns?: 2 | 3;
    tokensGridColumns?: number;
    showSavingsBadge?: boolean;
    showPerks?: boolean;
    compactMobile?: boolean;
    maxVisibleItems?: number;
}

export interface ShopCheckoutSettings {
    enableLoyaltyPoints?: boolean;
    showPromoCode?: boolean;
    showGiftOption?: boolean;
    showLoyaltyDiscount?: boolean;
    loyaltyTiers: {
        purchases?: number;
        purchasesRequired?: number;
        discountPercent: number;
    }[];
    enableGifting?: boolean;
}

export interface ShopConfig {
    globalSale: ShopGlobalSale;

    // Packages
    tokenPackages: ShopTokenPackage[];
    unlimitedPackages: ShopUnlimitedPackage[];
    premiumPackages: ShopPremiumPackage[];
    maxPackages: ShopMaxPackage[];

    // Benefits Showcases
    tokenBenefits?: ShopBenefit[];
    unlimitedBenefits?: ShopBenefit[];
    backgroundBenefits?: ShopBenefit[];
    premiumBenefits?: ShopBenefit[]; // Shared for Premium/MAX usually

    displaySettings: ShopDisplaySettings;
    checkout: ShopCheckoutSettings;
}

export const DEFAULT_SHOP_CONFIG: ShopConfig = {
    globalSale: {
        isActive: false,
        discountPercent: 0,
        title: 'Flash Sale!',
        endsAt: null
    },

    tokenPackages: [
        {
            id: 'token-starter',
            name: 'Starter Pack',
            tokens: 2000,
            price: 49,
            description: 'Perfect for a quick study session',
            perks: ['No expiration', 'Use anytime'],
            iconType: 'coffee',
            color: 'slate',
            isActive: true,
            displayOrder: 1
        },
        {
            id: 'token-value',
            name: 'Value Pack',
            tokens: 5000,
            price: 99,
            originalPrice: 125,
            savings: 20,
            description: 'Most popular choice for students',
            perks: ['No expiration', 'Best value'],
            iconType: 'book',
            color: 'blue',
            badge: 'POPULAR',
            badgeColor: 'blue',
            popular: true,
            highlight: true,
            isActive: true,
            displayOrder: 2
        },
        {
            id: 'token-pro',
            name: 'Pro Pack',
            tokens: 15000,
            price: 249,
            originalPrice: 375,
            savings: 33,
            description: 'For intense review sessions',
            perks: ['No expiration', 'Huge savings'],
            iconType: 'rocket',
            color: 'purple',
            isActive: true,
            displayOrder: 3
        }
    ],

    unlimitedPackages: [
        {
            id: 'unl-1h',
            name: '1 Hour',
            duration: 60,
            price: 29,
            description: 'Quick power hour',
            isActive: true,
            displayOrder: 1
        },
        {
            id: 'unl-3h',
            name: '3 Hours',
            duration: 180,
            price: 69,
            description: 'Deep focus session',
            isActive: true,
            popular: true,
            displayOrder: 2
        },
        {
            id: 'unl-24h',
            name: '24 Hours',
            duration: 1440,
            price: 149,
            description: 'All-day access',
            isActive: true,
            displayOrder: 3
        }
    ],

    premiumPackages: [
        // ==================== ALIGNED WITH BUSINESS_MONETIZATION_PLAN.md ====================
        // Premium: ₱149/month | ₱1,199/year (~₱100/mo)
        {
            id: 'premium-1m',
            name: '1 Month',
            duration: 30,
            price: 149,
            description: 'Less than ₱5/day - cheaper than coffee!',
            perks: ['15,000 daily tokens', 'All AI models', 'Priority support', 'Unlimited attachments'],
            isActive: true,
            displayOrder: 1
        },
        {
            id: 'premium-3m',
            name: '3 Months',
            duration: 90,
            price: 399,
            originalPrice: 447,
            description: 'Most popular premium duration',
            perks: ['15,000 daily tokens', 'All AI models', 'Priority support', '11% savings'],
            popular: true,
            savings: 11,
            isActive: true,
            displayOrder: 2
        },
        {
            id: 'premium-6m',
            name: '6 Months',
            duration: 180,
            price: 699,
            originalPrice: 894,
            description: 'Best value for serious learners',
            perks: ['15,000 daily tokens', 'All AI models', 'Priority support', '22% savings'],
            savings: 22,
            isActive: true,
            displayOrder: 3
        },
        {
            id: 'premium-12m',
            name: '1 Year',
            duration: 365,
            price: 1199,
            originalPrice: 1788,
            description: 'Ultimate commitment - ₱100/month!',
            perks: ['15,000 daily tokens', 'All AI models', 'VIP support', '33% savings', 'Exclusive features'],
            savings: 33,
            isActive: true,
            displayOrder: 4
        }
    ],

    maxPackages: [
        // ==================== ALIGNED WITH BUSINESS_MONETIZATION_PLAN.md ====================
        // MAX: ₱399/month | ₱2,999/year (~₱250/mo)
        {
            id: 'max-1m',
            name: '1 Month',
            duration: 30,
            price: 399,
            description: 'Ultimate power for professionals',
            perks: ['200,000 daily tokens', '20,000 tokens/hour refresh', 'All AI models', 'VIP support'],
            isActive: true,
            displayOrder: 1
        },
        {
            id: 'max-3m',
            name: '3 Months',
            duration: 90,
            price: 999,
            originalPrice: 1197,
            description: 'Best for CPA exam preparation period',
            perks: ['200,000 daily tokens', '20,000 tokens/hour refresh', 'All AI models', 'VIP priority', '17% savings'],
            popular: true,
            savings: 17,
            isActive: true,
            displayOrder: 2
        },
        {
            id: 'max-6m',
            name: '6 Months',
            duration: 180,
            price: 1799,
            originalPrice: 2394,
            description: 'Serious learners choice',
            perks: ['200,000 daily tokens', '20,000 tokens/hour refresh', 'All AI models', 'VIP priority', '25% savings'],
            savings: 25,
            isActive: true,
            displayOrder: 3
        },
        {
            id: 'max-12m',
            name: '1 Year',
            duration: 365,
            price: 2999,
            originalPrice: 4788,
            description: 'Ultimate power - ₱250/month!',
            perks: ['200,000 daily tokens', '20,000 tokens/hour refresh', 'All AI models', 'VIP priority', '37% savings', 'Early feature access'],
            savings: 37,
            isActive: true,
            displayOrder: 4
        }
    ],

    displaySettings: {
        tokensGridColumns: 1,
        showDescriptions: true,
        showPerks: true,
        showSavingsBadge: true,
        compactMobile: true,
        maxVisibleItems: -1
    },

    checkout: {
        showPromoCode: true,
        showGiftOption: true,
        showLoyaltyDiscount: true,
        loyaltyTiers: [
            { purchases: 1, discountPercent: 10 },
            { purchases: 2, discountPercent: 15 },
            { purchases: 3, discountPercent: 20 }
        ]
    }
};

// ==================== MODAL CONFIGURATION ====================

export interface TokenModalConfig {
    // Header configurations for different tabs
    tokensHeader: {
        title: string;
        subtitle: string;
        gradient: string;
        icon: string;
    };
    premiumHeader: {
        title: string;
        subtitle: string;
        gradient: string;
        icon: string;
    };
    maxHeader: {
        title: string;
        subtitle: string;
        gradient: string;
        icon: string;
    };

    // Content sections
    enableLowTokenPrompt: boolean;
    lowTokenMessage: string;
    enableLoyaltyDiscount: boolean;
    loyaltyDiscountPercent: number;

    // Call-to-action
    ctaMessage: string;
    urgencyMessage: string;

    // Display options
    showPayPalButton: boolean;
    showPromoCodeInput: boolean;
    showPackageComparison: boolean;
}

export const DEFAULT_TOKEN_MODAL_CONFIG: TokenModalConfig = {
    tokensHeader: {
        title: 'Get More Tokens',
        subtitle: 'Power up your AI study sessions',
        gradient: 'from-amber-500 to-yellow-500',
        icon: '⚡'
    },
    premiumHeader: {
        title: 'Premium Access',
        subtitle: '5x more tokens & advanced features',
        gradient: 'from-amber-500 to-orange-500',
        icon: '👑'
    },
    maxHeader: {
        title: 'MAX Tier',
        subtitle: '5x tokens & ultimate features',
        gradient: 'from-pink-500 to-rose-500',
        icon: '💎'
    },
    enableLowTokenPrompt: true,
    lowTokenMessage: 'Running low! Top up to continue your study session.',
    enableLoyaltyDiscount: true,
    loyaltyDiscountPercent: 10,
    ctaMessage: 'Choose your package to continue learning',
    urgencyMessage: 'Limited time offer - Get bonus tokens!',
    showPayPalButton: true,
    showPromoCodeInput: true,
    showPackageComparison: true
};

// ==================== MONETIZATION CONFIGURATION ====================
// Controls strategic moments for purchase prompts and engagement rewards

export interface MonetizationConfig {
    // Token Depletion Triggers - Strategic moments to show purchase prompts
    depletionTriggers: {
        // Percentage thresholds
        lowTokenWarning: number;       // Show soft warning (default: 30%)
        criticalTokenWarning: number;  // Show urgent warning (default: 15%)
        emptyTokenBlock: number;       // Block and show purchase (default: 5%)

        // Message-based triggers
        messagesBeforePrompt: number;  // Show upgrade prompt after X messages (free users)
        complexQueriesBeforePrompt: number; // Show after X complex queries

        // Time-based triggers
        sessionMinutesBeforePrompt: number; // Show after X minutes in session
        dailyPromptsLimit: number;     // Max purchase prompts per day
    };

    // Purchase Prompt Messages
    promptMessages: {
        lowTokens: {
            title: string;
            message: string;
            ctaText: string;
            urgency: 'low' | 'medium' | 'high';
        };
        criticalTokens: {
            title: string;
            message: string;
            ctaText: string;
            urgency: 'low' | 'medium' | 'high';
        };
        emptyTokens: {
            title: string;
            message: string;
            ctaText: string;
            urgency: 'low' | 'medium' | 'high';
        };
        midTask: {
            title: string;
            message: string;
            ctaText: string;
            urgency: 'low' | 'medium' | 'high';
        };
        upgradeNudge: {
            title: string;
            message: string;
            ctaText: string;
            urgency: 'low' | 'medium' | 'high';
        };
    };

    // Quality-Based Pricing Messages
    qualityPricing: {
        enabled: boolean;
        messages: {
            simple: string;     // "Quick answer - minimal tokens"
            moderate: string;   // "Detailed explanation"
            complex: string;    // "Expert analysis"
            comprehensive: string; // "Full professional consultation"
        };
    };

    // Engagement Rewards
    engagementRewards: {
        enabled: boolean;

        // Daily login bonus
        dailyLoginBonus: number;       // Tokens for daily login
        consecutiveDaysMultiplier: number; // Multiply by consecutive days (max 7)
        maxConsecutiveBonus: number;   // Cap on consecutive bonus

        // Activity rewards
        firstMessageOfDay: number;     // Bonus for first message
        quizCompletion: number;        // Bonus for completing quiz
        examCompletion: number;        // Bonus for completing exam
        sessionMilestones: {           // Bonus for session length
            minutes15: number;
            minutes30: number;
            minutes60: number;
        };

        // Referral rewards
        referralBonus: number;         // Tokens when referred user signs up
        referredUserBonus: number;     // Tokens for new user who was referred

        // Achievement bonuses
        firstPurchaseBonus: number;    // Extra tokens on first purchase
        loyaltyMultiplier: number;     // Multiply purchases by this after X purchases
        loyaltyPurchaseThreshold: number; // Purchases needed for loyalty
    };

    // Upgrade Incentives
    upgradeIncentives: {
        // Show comparison when free user hits limit
        showComparisonOnLimit: boolean;

        // Highlight savings
        showYearlySavings: boolean;
        yearlySavingsMessage: string;  // "Save {X}% with yearly plan!"

        // Trial offers
        offerFreeTrial: boolean;
        freeTrialDays: number;
        freeTrialTokens: number;

        // One-time upgrade discount
        firstUpgradeDiscount: number;  // % off first upgrade
        firstUpgradeMessage: string;
    };

    // Smart Nudges - Context-aware suggestions
    smartNudges: {
        enabled: boolean;

        // When to show
        afterComplexAnswer: boolean;   // "Expert mode would give even more detail"
        afterAttachmentAnalysis: boolean; // "Unlimited attachments with Premium"
        afterQuizGeneration: boolean;  // "Generate unlimited quizzes"

        // Cooldown
        nudgeCooldownMinutes: number;  // Don't show another nudge for X minutes
        maxNudgesPerSession: number;   // Max nudges in one session
    };

    // Value Demonstration
    valueDemo: {
        // Show token savings
        showTokenSaved: boolean;       // "You saved X tokens with Premium!"

        // Quality comparison
        showQualityComparison: boolean; // Show basic vs premium response

        // Progress tracking
        showProgressStats: boolean;    // "You've learned X concepts today"
        showStreakRewards: boolean;    // "5-day streak! Keep going!"
    };
}

export const DEFAULT_MONETIZATION_CONFIG: MonetizationConfig = {
    depletionTriggers: {
        lowTokenWarning: 30,
        criticalTokenWarning: 15,
        emptyTokenBlock: 5,
        messagesBeforePrompt: 10,
        complexQueriesBeforePrompt: 3,
        sessionMinutesBeforePrompt: 30,
        dailyPromptsLimit: 5,
    },

    promptMessages: {
        lowTokens: {
            title: '📊 Tokens Running Low',
            message: 'You have {remaining} tokens left. Top up to continue your study session uninterrupted!',
            ctaText: 'Get More Tokens',
            urgency: 'low',
        },
        criticalTokens: {
            title: '⚠️ Almost Out of Tokens!',
            message: 'Only {remaining} tokens remaining. Don\'t lose your momentum - get more now!',
            ctaText: 'Top Up Now',
            urgency: 'medium',
        },
        emptyTokens: {
            title: '🔒 You\'ve Run Out of Tokens',
            message: 'Your study session was going great! Get tokens to continue where you left off.',
            ctaText: 'Continue Studying',
            urgency: 'high',
        },
        midTask: {
            title: '📚 Don\'t Stop Now!',
            message: 'You\'re in the middle of learning {topic}. Get tokens to complete your session.',
            ctaText: 'Keep Learning',
            urgency: 'high',
        },
        upgradeNudge: {
            title: '⭐ Unlock More with Premium',
            message: 'Premium users get 10x more tokens and exclusive features. Upgrade today!',
            ctaText: 'View Plans',
            urgency: 'low',
        },
    },

    qualityPricing: {
        enabled: true,
        messages: {
            simple: '⚡ Quick Answer - {cost} tokens',
            moderate: '📝 Detailed Explanation - {cost} tokens',
            complex: '🔬 Expert Analysis - {cost} tokens',
            comprehensive: '📚 Full Consultation - {cost} tokens',
        },
    },

    engagementRewards: {
        // ==================== ALIGNED WITH BUSINESS_MONETIZATION_PLAN.md ====================
        // Total possible daily bonus: 200-400 tokens (extends free tier by ~30%)
        enabled: true,
        dailyLoginBonus: 50,              // Daily login bonus
        consecutiveDaysMultiplier: 1.3,   // 7-day streak = up to +200 bonus
        maxConsecutiveBonus: 200,         // Cap on streak bonus
        firstMessageOfDay: 25,            // First message/day
        quizCompletion: 30,               // Quiz completion
        examCompletion: 50,               // Exam completion (big accomplishment)
        sessionMilestones: {
            minutes15: 20,                 // 15 min session
            minutes30: 40,                 // 30 min session
            minutes60: 80,                 // 1 hour session (Hour of Power!)
        },
        referralBonus: 500,               // Referral signup bonus
        referredUserBonus: 250,           // Bonus for new user who was referred
        firstPurchaseBonus: 100,          // First purchase bonus
        loyaltyMultiplier: 1.1,           // 10% extra after 3+ purchases
        loyaltyPurchaseThreshold: 3,      // Purchases needed for loyalty
    },

    upgradeIncentives: {
        showComparisonOnLimit: true,
        showYearlySavings: true,
        yearlySavingsMessage: 'Save 30% with yearly plan!',
        offerFreeTrial: true,
        freeTrialDays: 3,
        freeTrialTokens: 5000,
        firstUpgradeDiscount: 20,
        firstUpgradeMessage: '🎉 First-time upgrade? Get 20% off!',
    },

    smartNudges: {
        enabled: true,
        afterComplexAnswer: true,
        afterAttachmentAnalysis: true,
        afterQuizGeneration: true,
        nudgeCooldownMinutes: 15,
        maxNudgesPerSession: 3,
    },

    valueDemo: {
        showTokenSaved: true,
        showQualityComparison: false,
        showProgressStats: true,
        showStreakRewards: true,
    },
};

// ==================== COMPLETE CONFIG BUNDLE ====================

export interface CentralConfig {
    ranks: RankConfig[];
    tokenConsumption: TokenConsumptionConfig;
    system: SystemConfig;
    modal: TokenModalConfig;
    landing: LandingPageContentConfig;
    shop: ShopConfig;
    monetization: MonetizationConfig;
    mastery?: import('./masteryConfig').MasteryPointsConfig;
    lastUpdated: string;
    version: string;
}

export const DEFAULT_CENTRAL_CONFIG: CentralConfig = {
    ranks: DEFAULT_RANK_CONFIGS,
    tokenConsumption: DEFAULT_TOKEN_CONSUMPTION_CONFIG,
    system: DEFAULT_SYSTEM_CONFIG,
    modal: DEFAULT_TOKEN_MODAL_CONFIG,
    landing: DEFAULT_LANDING_CONFIG,
    shop: DEFAULT_SHOP_CONFIG,
    monetization: DEFAULT_MONETIZATION_CONFIG,
    lastUpdated: new Date().toISOString(),
    version: '2.2.0',
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get rank configuration by tier ID
 */
export function getRankConfig(ranks: RankConfig[], tierId: AccountTier): RankConfig {
    return ranks.find(r => r.id === tierId) || ranks[0];
}

/**
 * Get user's rank based on their account properties
 */
export function getUserRankFromConfig(
    ranks: RankConfig[],
    user: { isMax?: boolean; isMaxTier?: boolean; accountTier?: AccountTier; isPremium?: boolean }
): RankConfig {
    // First check explicit accountTier
    if (user.accountTier === 'max') {
        return getRankConfig(ranks, 'max');
    }
    if (user.accountTier === 'premium') {
        return getRankConfig(ranks, 'premium');
    }
    if (user.accountTier === 'standard') {
        return getRankConfig(ranks, 'standard');
    }

    // Then check boolean flags (legacy)
    if (user.isMax || user.isMaxTier) {
        return getRankConfig(ranks, 'max');
    }
    if (user.isPremium) {
        return getRankConfig(ranks, 'premium');
    }
    return getRankConfig(ranks, 'standard');
}

/**
 * Get token limit for a user based on their rank
 */
export function getTokenLimitFromConfig(ranks: RankConfig[], user: any): number {
    const rank = getUserRankFromConfig(ranks, user);
    return rank.tokenLimit;
}

/**
 * Get refresh rate for a user based on their rank
 */
export function getRefreshRateFromConfig(ranks: RankConfig[], user: any): number {
    const rank = getUserRankFromConfig(ranks, user);
    return rank.tokenRefreshRate;
}

/**
 * Get colors for a user based on their rank
 */
export function getUserColorsFromConfig(ranks: RankConfig[], user: any): RankConfig['colors'] {
    const rank = getUserRankFromConfig(ranks, user);
    return rank.colors;
}

/**
 * Merge partial config with defaults
 */
export function mergeWithDefaults(partial: Partial<CentralConfig>): CentralConfig {
    return {
        ranks: partial.ranks || DEFAULT_RANK_CONFIGS,
        tokenConsumption: {
            ...DEFAULT_TOKEN_CONSUMPTION_CONFIG,
            ...partial.tokenConsumption,
        },
        system: {
            ...DEFAULT_SYSTEM_CONFIG,
            ...partial.system,
        },
        modal: mergeModalConfig(partial.modal),
        landing: {
            ...DEFAULT_LANDING_CONFIG,
            ...partial.landing,
        },
        shop: mergeShopConfig(partial.shop),
        monetization: mergeMonetizationConfig(partial.monetization),
        lastUpdated: new Date().toISOString(),
        version: partial.version || '2.2.0',
    };
}

/**
 * Deep merge monetization config with defaults
 */
export function mergeMonetizationConfig(partial: Partial<MonetizationConfig> | undefined | null): MonetizationConfig {
    if (!partial || typeof partial !== 'object') {
        return { ...DEFAULT_MONETIZATION_CONFIG };
    }

    return {
        depletionTriggers: {
            ...DEFAULT_MONETIZATION_CONFIG.depletionTriggers,
            ...(partial.depletionTriggers || {})
        },
        promptMessages: {
            lowTokens: { ...DEFAULT_MONETIZATION_CONFIG.promptMessages.lowTokens, ...(partial.promptMessages?.lowTokens || {}) },
            criticalTokens: { ...DEFAULT_MONETIZATION_CONFIG.promptMessages.criticalTokens, ...(partial.promptMessages?.criticalTokens || {}) },
            emptyTokens: { ...DEFAULT_MONETIZATION_CONFIG.promptMessages.emptyTokens, ...(partial.promptMessages?.emptyTokens || {}) },
            midTask: { ...DEFAULT_MONETIZATION_CONFIG.promptMessages.midTask, ...(partial.promptMessages?.midTask || {}) },
            upgradeNudge: { ...DEFAULT_MONETIZATION_CONFIG.promptMessages.upgradeNudge, ...(partial.promptMessages?.upgradeNudge || {}) },
        },
        qualityPricing: {
            ...DEFAULT_MONETIZATION_CONFIG.qualityPricing,
            ...(partial.qualityPricing || {}),
            messages: {
                ...DEFAULT_MONETIZATION_CONFIG.qualityPricing.messages,
                ...(partial.qualityPricing?.messages || {})
            }
        },
        engagementRewards: {
            ...DEFAULT_MONETIZATION_CONFIG.engagementRewards,
            ...(partial.engagementRewards || {}),
            sessionMilestones: {
                ...DEFAULT_MONETIZATION_CONFIG.engagementRewards.sessionMilestones,
                ...(partial.engagementRewards?.sessionMilestones || {})
            }
        },
        upgradeIncentives: {
            ...DEFAULT_MONETIZATION_CONFIG.upgradeIncentives,
            ...(partial.upgradeIncentives || {})
        },
        smartNudges: {
            ...DEFAULT_MONETIZATION_CONFIG.smartNudges,
            ...(partial.smartNudges || {})
        },
        valueDemo: {
            ...DEFAULT_MONETIZATION_CONFIG.valueDemo,
            ...(partial.valueDemo || {})
        },
    };
}

/**
 * Deep merge shop config with defaults
 */
export function mergeShopConfig(partial: Partial<ShopConfig> | undefined | null): ShopConfig {
    if (!partial || typeof partial !== 'object') {
        return { ...DEFAULT_SHOP_CONFIG };
    }

    return {
        globalSale: {
            isActive: partial.globalSale?.isActive ?? DEFAULT_SHOP_CONFIG.globalSale?.isActive ?? false,
            discountPercent: partial.globalSale?.discountPercent ?? DEFAULT_SHOP_CONFIG.globalSale?.discountPercent ?? 0,
            title: partial.globalSale?.title ?? DEFAULT_SHOP_CONFIG.globalSale?.title ?? 'Flash Sale!',
            endsAt: partial.globalSale?.endsAt ?? DEFAULT_SHOP_CONFIG.globalSale?.endsAt ?? null
        },
        tokenPackages: partial.tokenPackages || DEFAULT_SHOP_CONFIG.tokenPackages,
        unlimitedPackages: partial.unlimitedPackages || DEFAULT_SHOP_CONFIG.unlimitedPackages,
        premiumPackages: partial.premiumPackages || DEFAULT_SHOP_CONFIG.premiumPackages,
        maxPackages: partial.maxPackages || DEFAULT_SHOP_CONFIG.maxPackages,
        displaySettings: {
            ...DEFAULT_SHOP_CONFIG.displaySettings,
            ...(partial.displaySettings || {})
        },
        checkout: {
            ...DEFAULT_SHOP_CONFIG.checkout,
            ...(partial.checkout || {}),
            loyaltyTiers: partial.checkout?.loyaltyTiers || DEFAULT_SHOP_CONFIG.checkout.loyaltyTiers
        }
    };
}

/**
 * Deep merge modal config with defaults to ensure all nested properties exist
 */
export function mergeModalConfig(partial: Partial<TokenModalConfig> | undefined | null): TokenModalConfig {
    if (!partial || typeof partial !== 'object') {
        return { ...DEFAULT_TOKEN_MODAL_CONFIG };
    }

    return {
        tokensHeader: {
            ...DEFAULT_TOKEN_MODAL_CONFIG.tokensHeader,
            ...(partial.tokensHeader || {})
        },
        premiumHeader: {
            ...DEFAULT_TOKEN_MODAL_CONFIG.premiumHeader,
            ...(partial.premiumHeader || {})
        },
        maxHeader: {
            ...DEFAULT_TOKEN_MODAL_CONFIG.maxHeader,
            ...(partial.maxHeader || {})
        },
        enableLowTokenPrompt: partial.enableLowTokenPrompt ?? DEFAULT_TOKEN_MODAL_CONFIG.enableLowTokenPrompt,
        lowTokenMessage: partial.lowTokenMessage ?? DEFAULT_TOKEN_MODAL_CONFIG.lowTokenMessage,
        enableLoyaltyDiscount: partial.enableLoyaltyDiscount ?? DEFAULT_TOKEN_MODAL_CONFIG.enableLoyaltyDiscount,
        loyaltyDiscountPercent: partial.loyaltyDiscountPercent ?? DEFAULT_TOKEN_MODAL_CONFIG.loyaltyDiscountPercent,
        ctaMessage: partial.ctaMessage ?? DEFAULT_TOKEN_MODAL_CONFIG.ctaMessage,
        urgencyMessage: partial.urgencyMessage ?? DEFAULT_TOKEN_MODAL_CONFIG.urgencyMessage,
        showPayPalButton: partial.showPayPalButton ?? DEFAULT_TOKEN_MODAL_CONFIG.showPayPalButton,
        showPromoCodeInput: partial.showPromoCodeInput ?? DEFAULT_TOKEN_MODAL_CONFIG.showPromoCodeInput,
        showPackageComparison: partial.showPackageComparison ?? DEFAULT_TOKEN_MODAL_CONFIG.showPackageComparison,
    };
}

/**
 * Load central config from localStorage with proper defaults and auto-migration
 */
export function loadCentralConfig(): CentralConfig {
    try {
        const stored = localStorage.getItem('luca_central_config');
        if (stored) {
            const parsed = JSON.parse(stored);
            const config = mergeWithDefaults(parsed);

            // AUTO-MIGRATE: Update old gemini-2.0-flash to gemini-2.5-flash-lite
            let needsSave = false;

            if (config.system.defaultApiModel === 'gemini-2.0-flash') {
                config.system.defaultApiModel = 'gemini-2.5-flash-lite';
                needsSave = true;
                console.log('[CentralConfig] Auto-migrated defaultApiModel to gemini-2.5-flash-lite');
            }

            if (config.system.fallbackApiModel === 'gemini-2.0-flash') {
                config.system.fallbackApiModel = 'gemini-2.5-flash-lite';
                needsSave = true;
                console.log('[CentralConfig] Auto-migrated fallbackApiModel to gemini-2.5-flash-lite');
            }

            // Migrate rank configs
            config.ranks.forEach(rank => {
                if (rank.defaultModel === 'gemini-2.0-flash') {
                    rank.defaultModel = 'gemini-2.5-flash-lite';
                    needsSave = true;
                    console.log(`[CentralConfig] Auto-migrated rank ${rank.id} to gemini-2.5-flash-lite`);
                }
            });

            // Save if migrated
            if (needsSave) {
                saveCentralConfig(config);
                console.log('[CentralConfig] Auto-migration complete, config saved');
            }

            return config;
        }
    } catch (error) {
        console.error('[CentralConfig] Failed to load config:', error);
    }
    return DEFAULT_CENTRAL_CONFIG;
}

/**
 * Save central config to localStorage
 */
export function saveCentralConfig(config: CentralConfig): void {
    try {
        config.lastUpdated = new Date().toISOString();
        localStorage.setItem('luca_central_config', JSON.stringify(config));
        console.log('[CentralConfig] Saved to localStorage');
    } catch (error) {
        console.error('[CentralConfig] Failed to save config:', error);
    }
}

